import type { ReactNode } from 'react';
import type { Outcome } from '../../core/analysis';
import { OUTCOME_LABEL } from '../../core/feedback';
import { Icon, type IconName } from './Icon';

const OUTCOME_ICON: Record<Outcome, IconName> = { clean: 'checkCircle', adjust: 'arrows', unassessed: 'eyeOff', none: 'minus' };

export function OutcomeBadge({ outcome, large }: { outcome: Outcome; large?: boolean }) {
  return (
    <span className={`badge ${outcome}${large ? ' lg' : ''}`}>
      <Icon name={OUTCOME_ICON[outcome]} size={large ? 18 : 15} stroke={2.4} />
      {OUTCOME_LABEL[outcome]}
    </span>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" aria-label={label} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span />
    </label>
  );
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  block,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  block?: boolean;
  label: string;
}) {
  return (
    <div className={`segmented${block ? ' block' : ''}`} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'warn' | 'error' | 'good'; children: ReactNode }) {
  const icon: IconName = kind === 'warn' ? 'alert' : kind === 'error' ? 'alert' : kind === 'good' ? 'checkCircle' : 'info';
  return (
    <div className={`notice ${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      <Icon name={icon} size={18} />
      <div>{children}</div>
    </div>
  );
}

export function Sheet({ onClose, children, label }: { onClose: () => void; children: ReactNode; label: string }) {
  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function PageHead({ title, back, children }: { title: string; back?: () => void; children?: ReactNode }) {
  return (
    <div className="page-head">
      {back && (
        <button className="icon-btn" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
      )}
      <h1>{title}</h1>
      {children}
    </div>
  );
}

export function Ring({ value, label }: { value: number; label: ReactNode }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  return (
    <div className="ring" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value * 100)}>
      <svg width={88} height={88}>
        <circle cx={44} cy={44} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={7} />
        <circle cx={44} cy={44} r={r} fill="none" stroke="var(--accent)" strokeWidth={7} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - value)} />
      </svg>
      <b>{label}</b>
    </div>
  );
}

export const fmtDate = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
export const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
export const fmtPct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
export const fmtSec = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(2)} s`);
export const fmtCm = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `≈${Math.round(v)} cm`);
